import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useCreateCharacter } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/cn';
import { hapticNotify } from '@/lib/telegram';
import type { Gender } from '@/types';

interface BackstoryPayload {
  backstory: string;
}

export function CharacterCreationScreen() {
  const userId = useAuthStore((s) => s.userId);
  const username = useAuthStore((s) => s.username);
  const setScreen = useUIStore((s) => s.setScreen);
  const openModal = useUIStore((s) => s.openModal);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);

  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [appearance, setAppearance] = useState('');
  const create = useCreateCharacter();

  const submit = async () => {
    if (!userId || !gender) return;
    if (name.trim().length < 2) {
      pushNotification({ type: 'warning', message: 'Имя должно содержать минимум 2 символа' });
      return;
    }
    if (appearance.trim().length < 10) {
      pushNotification({
        type: 'warning',
        message: 'Описание внешности должно содержать минимум 10 символов',
      });
      return;
    }
    setGlobalLoading(true, 'Создаём персонажа и waifu… это займёт ~30 секунд');
    try {
      const data = await create.mutateAsync({
        user_id: userId,
        username: username ?? undefined,
        character_name: name.trim(),
        gender,
        appearance: appearance.trim(),
      });
      hapticNotify('success');
      if (data.backstory) {
        openModal('avatar', { backstory: data.backstory } satisfies BackstoryPayload);
      }
      setScreen('backstory');
    } catch (err) {
      hapticNotify('error');
      const message = err instanceof Error ? err.message : 'Не удалось создать персонажа';
      pushNotification({ type: 'error', message });
    } finally {
      setGlobalLoading(false);
    }
  };

  const disabled =
    create.isPending || !gender || name.trim().length < 2 || appearance.trim().length < 10;

  return (
    <div className="flex-1 overflow-y-auto p-4 pb-8">
      <div className="max-w-md mx-auto space-y-4">
        <header className="text-center pt-4 pb-2">
          <h1 className="font-display text-3xl text-white">Создание персонажа</h1>
          <p className="text-sm text-white/50 mt-1">
            Опиши себя — мир Isekai ждёт.
          </p>
        </header>

        <Card className="p-4 space-y-4">
          <label className="block space-y-1">
            <span className="text-sm text-white/70">Имя персонажа</span>
            <input
              className="input"
              maxLength={50}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Kazuto"
            />
          </label>

          <div className="space-y-1">
            <span className="text-sm text-white/70">Пол</span>
            <div className="grid grid-cols-2 gap-2">
              {(['male', 'female'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={cn(
                    'rounded-xl py-3 px-4 border transition text-white font-medium',
                    gender === g
                      ? 'bg-accent border-accent shadow-glow'
                      : 'bg-bg-elevated border-white/5 hover:border-white/20',
                  )}
                >
                  {g === 'male' ? '♂ Мужской' : '♀ Женский'}
                </button>
              ))}
            </div>
          </div>

          <label className="block space-y-1">
            <span className="text-sm text-white/70">
              Внешность <span className="text-white/40">({appearance.length}/500)</span>
            </span>
            <textarea
              className="input min-h-[120px] resize-none"
              maxLength={500}
              value={appearance}
              onChange={(e) => setAppearance(e.target.value)}
              placeholder="Опиши внешность: цвет волос, глаза, телосложение, стиль одежды..."
            />
          </label>

          <Button fullWidth size="lg" onClick={submit} disabled={disabled}>
            {create.isPending ? 'Создаём…' : 'Начать приключение'}
          </Button>
        </Card>
      </div>
    </div>
  );
}
